import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { Calendar as CalendarIcon, MapPin, Clock, Loader2, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';

export default function ArenaxVenueDetail() {
  const { venueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/arenax/venues/${venueId}`).then(r => setVenue(r.data)).catch(() => setVenue(null)).finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => {
    if (!venue) return;
    api.get(`/arenax/venues/${venue.id}/availability?date=${date}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
    setSelected([]);
  }, [venue, date]);

  const slotsByCourt = useMemo(() => {
    const map = {};
    (slots || []).forEach(s => {
      const cid = s.court_id;
      if (!map[cid]) map[cid] = [];
      map[cid].push(s);
    });
    return map;
  }, [slots]);

  const courtName = (cid) => (venue?.courts || []).find(c => c.id === cid)?.name || 'Court';
  const courtSport = (cid) => (venue?.courts || []).find(c => c.id === cid)?.sport || '';

  const totalPrice = useMemo(() => selected.reduce((acc, sid) => {
    const s = slots.find(x => x.id === sid); return acc + (s ? Number(s.price || 0) : 0);
  }, 0), [selected, slots]);

  const toggleSlot = (s) => {
    if (s.status !== 'available') return;
    setSelected(prev => prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id]);
  };

  const proceedToBook = async () => {
    if (!user) {
      navigate(`/arenax/auth/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (!user) {
      toast.error('Please sign in with a player account to book.');
      return;
    }
    if (selected.length === 0) { toast.error('Pick at least one slot'); return; }
    try {
      const r = await api.post('/arenax/bookings/lock', { slot_ids: selected });
      setBooking(r.data.booking);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not lock slots');
    }
  };

  const doCheckout = async () => {
    if (!booking) return;
    try {
      const r = await api.post('/arenax/bookings/checkout', { booking_id: booking.id });
      if (r.data.mock_mode) {
        // Mock pay flow: directly confirm
        const c = await api.post('/arenax/bookings/confirm', {
          booking_id: booking.id, razorpay_order_id: r.data.order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: 'mock',
        });
        setConfirmation(c.data.booking);
        toast.success('Booking confirmed (mock pay)');
      } else {
        // Real Razorpay
        if (!window.Razorpay) {
          await loadRazorpay();
        }
        const options = {
          key: r.data.key_id, amount: r.data.amount, currency: r.data.currency,
          order_id: r.data.order_id, name: 'ArenaX · Out-Play',
          description: `${venue.name} · ${selected.length} slot(s)`,
          handler: async (resp) => {
            try {
              const c = await api.post('/arenax/bookings/confirm', {
                booking_id: booking.id, razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature,
              });
              setConfirmation(c.data.booking);
              toast.success('Booking confirmed!');
            } catch {
              toast.error('Payment processed but confirmation failed. Check My Bookings.');
            }
          },
          theme: { color: '#06b6d4' },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    }
  };

  if (loading) return <ArenaxLayout><div className="text-center text-slate-500 py-32">Loading venue...</div></ArenaxLayout>;
  if (!venue) return <ArenaxLayout><div className="text-center py-32"><div className="text-slate-300 mb-3">Venue not found</div><Link to="/arenax/venues" className="text-cyan-300 text-sm">← Back to venues</Link></div></ArenaxLayout>;

  if (confirmation) {
    return (
      <ArenaxLayout>
        <section className="max-w-2xl mx-auto px-5 py-20 text-center">
          <div className="rounded-3xl p-10 border border-emerald-500/30"
            style={{ background: 'linear-gradient(160deg, rgba(16,185,129,0.08), var(--ax-card-bg-soft))' }}>
            <div className="inline-flex h-16 w-16 rounded-2xl items-center justify-center mb-5 bg-emerald-500/20 border border-emerald-500/40">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-3xl font-black text-slate-100 mb-2">Booking confirmed</h2>
            <p className="text-sm text-slate-400 mb-6">Code: <span className="text-cyan-300 font-mono font-bold">{confirmation.booking_code}</span></p>
            <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-5 text-left mb-6">
              <div className="text-xs text-slate-500 mb-1">{venue.name}</div>
              {confirmation.slots_snapshot.map(s => (
                <div key={s.id} className="text-sm text-slate-200 flex items-center justify-between py-1">
                  <span>{s.date} · {s.start_time}–{s.end_time}</span>
                  <span className="text-cyan-300">₹{s.price}</span>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between">
                <span className="text-xs text-slate-500">Total Paid</span>
                <span className="text-lg font-black text-slate-100">₹{Number(confirmation.total_amount || 0).toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <Link to="/arenax/player/bookings"><button className="px-6 py-3 rounded-xl font-bold text-slate-950 text-sm" style={{ background: 'var(--ax-primary-gradient)' }}>View My Bookings</button></Link>
              <Link to="/arenax/venues"><button className="px-6 py-3 rounded-xl border border-cyan-500/40 text-cyan-300 text-sm font-bold">Discover More</button></Link>
            </div>
          </div>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        {/* Hero */}
        <div className="rounded-3xl overflow-hidden border border-cyan-500/15 mb-8" data-testid="arenax-venue-hero">
          <div className="aspect-[16/6] relative" style={{
            background: venue.cover_image_url ? `url(${venue.cover_image_url}) center/cover` : 'linear-gradient(135deg, #0e7490 0%, #f59e0b 100%)',
          }}>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
            <div className="absolute bottom-5 left-6 right-6">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(venue.sports_supported || []).map(s => (
                  <span key={s} className="text-[10px] font-bold px-2 py-1 rounded-md bg-cyan-500/20 backdrop-blur-md border border-cyan-500/30 text-cyan-200">{s}</span>
                ))}
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-slate-100">{venue.name}</h1>
              <div className="flex items-center gap-1.5 text-sm text-slate-300 mt-1">
                <MapPin className="h-4 w-4 text-cyan-400" />{venue.address || venue.city}
              </div>
            </div>
          </div>
        </div>

        {/* Date picker + slot grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <CalendarIcon className="h-4 w-4 text-cyan-400" />
              <span className="text-xs tracking-wider uppercase font-bold text-slate-400">Choose Date</span>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="arenax-venue-date"
                    className="ml-auto px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 hover:border-cyan-500 text-slate-100 text-sm focus:outline-none flex items-center gap-2 transition-colors">
                    <CalendarIcon className="h-3.5 w-3.5 text-cyan-400" />
                    {new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="end">
                  <Calendar
                    mode="single"
                    selected={new Date(date)}
                    onSelect={(d) => { if (d) setDate(d.toISOString().slice(0, 10)); }}
                    disabled={(d) => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }}
                    initialFocus
                    className="bg-slate-900 text-slate-100"
                    data-testid="arenax-venue-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {Object.keys(slotsByCourt).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-700 p-10 text-center">
                <Sparkles className="h-8 w-8 text-cyan-400/40 mx-auto mb-3" />
                <div className="text-slate-300 font-bold mb-1">No slots configured for this date</div>
                <div className="text-xs text-slate-500">The venue may not have published its schedule yet, or all slots are booked.</div>
              </div>
            ) : (
              <div className="space-y-5" data-testid="arenax-venue-slot-grid">
                {Object.entries(slotsByCourt).map(([cid, list]) => (
                  <div key={cid} className="rounded-2xl border border-slate-800 p-5"
                    style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-bold text-slate-100">{courtName(cid)}</div>
                      <span className="text-[10px] text-amber-300 tracking-wider">{courtSport(cid)}</span>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {list.map(s => {
                        const isSel = selected.includes(s.id);
                        const isBooked = s.status === 'booked';
                        const isLocked = s.status === 'locked';
                        const isPeak = s.is_peak;
                        return (
                          <button key={s.id} onClick={() => toggleSlot(s)} disabled={isBooked || isLocked}
                            data-testid={`arenax-slot-${s.id}`}
                            className={`px-2 py-2.5 rounded-lg text-[11px] font-bold transition-all text-left ${
                              isBooked ? 'bg-slate-800/60 text-slate-600 cursor-not-allowed line-through' :
                              isLocked ? 'bg-amber-500/10 text-amber-500/60 cursor-not-allowed' :
                              isSel ? 'bg-cyan-500 text-slate-950 ring-2 ring-cyan-300' :
                              'bg-slate-900/80 text-slate-200 hover:bg-slate-800 border border-slate-700 hover:border-cyan-500/50'
                            }`}>
                            <div className="flex items-center gap-1">
                              <Clock className="h-2.5 w-2.5 opacity-70" />
                              {s.start_time}
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="opacity-70">{s.end_time}</span>
                              {isPeak && !isBooked && <span className="text-amber-400">★</span>}
                            </div>
                            <div className="text-[10px] mt-0.5 opacity-80">₹{s.price}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sticky booking summary */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-2xl border border-cyan-500/20 p-5" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.07), var(--ax-card-bg-soft))' }} data-testid="arenax-booking-summary">
              <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-2">YOUR ORDER</div>
              {selected.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Pick one or more slots from the left.</div>
              ) : (
                <>
                  <div className="space-y-2 mb-4 max-h-64 overflow-y-auto pr-1">
                    {selected.map(sid => {
                      const s = slots.find(x => x.id === sid);
                      if (!s) return null;
                      return (
                        <div key={sid} className="flex items-center justify-between text-xs">
                          <div>
                            <div className="text-slate-200 font-semibold">{courtName(s.court_id)}</div>
                            <div className="text-slate-500">{s.start_time}–{s.end_time}</div>
                          </div>
                          <div className="text-cyan-300 font-bold">₹{s.price}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-slate-700 pt-3 mb-4 flex items-center justify-between">
                    <span className="text-xs text-slate-500">Total</span>
                    <span className="text-xl font-black text-slate-100">₹{Number(totalPrice || 0).toLocaleString('en-IN')}</span>
                  </div>
                </>
              )}
              {!booking ? (
                <button onClick={proceedToBook} disabled={selected.length === 0}
                  data-testid="arenax-book-cta"
                  className="w-full px-4 py-3 rounded-xl font-bold text-slate-950 text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                  style={{ background: 'var(--ax-primary-gradient)' }}>
                  {user?.id ? <>Lock Slots & Pay <ArrowRight className="h-4 w-4" /></> : <>Sign in to book <ArrowRight className="h-4 w-4" /></>}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="text-[11px] text-amber-300 font-semibold mb-1">⏱ Locked for 10 min · Code: <span className="font-mono">{booking.booking_code}</span></div>

                  {/* Phase 141-C — Booking Breakdown disclosure (tier-aware) */}
                  {(() => {
                    const isLegend = !!(booking.legend_waiver || booking.fee_tier_applied === 'legend');
                    const base = Number(booking.total_amount || 0);
                    const commission = Number(booking.platform_commission || 0);
                    const payout = Number(booking.venue_payout || (base - commission));
                    return (
                      <div
                        data-testid="arenax-booking-breakdown"
                        className={`rounded-xl border text-[12px] p-3 space-y-2 ${
                          isLegend
                            ? 'bg-emerald-500/[0.06] border-emerald-500/30 text-emerald-100'
                            : 'bg-slate-900/60 border-slate-700 text-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between pb-1.5 border-b border-dashed border-slate-700/60">
                          <span className="text-[10px] uppercase tracking-[0.18em] font-black text-slate-500">Booking Breakdown</span>
                          <span
                            data-testid="arenax-booking-breakdown-tier-pill"
                            className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                              isLegend ? 'bg-emerald-500 text-slate-950' : 'bg-slate-700 text-slate-200'
                            }`}
                          >
                            {isLegend ? 'Legend Member' : 'Standard Rate'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Base booking</span>
                          <span className="font-mono font-semibold tabular-nums">₹{base.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Platform fee</span>
                          <span
                            data-testid="arenax-booking-breakdown-platform-fee"
                            className={`font-mono font-semibold tabular-nums ${isLegend ? 'text-emerald-300' : 'text-slate-200'}`}
                          >
                            {isLegend ? '₹0 · 0% Legend waiver' : `₹${commission.toLocaleString('en-IN')}`}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-1.5 border-t border-slate-700/60">
                          <span className={`${isLegend ? 'text-emerald-200' : 'text-slate-200'} font-bold text-[12px]`}>Net to venue</span>
                          <span
                            data-testid="arenax-booking-breakdown-net-payout"
                            className={`font-mono tabular-nums ${
                              isLegend ? 'text-emerald-300 text-base font-black' : 'text-white text-sm font-black'
                            }`}
                          >
                            ₹{payout.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  <button onClick={doCheckout} data-testid="arenax-checkout-cta"
                    className="w-full px-4 py-3 rounded-xl font-bold text-slate-950 text-sm flex items-center justify-center gap-2"
                    style={{ background: 'var(--ax-primary-gradient)' }}>
                    Complete Payment <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div className="mt-3 text-[10px] text-slate-600 text-center">Secure payments via Razorpay · Powered by Out-Play</div>
            </div>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = resolve;
    document.body.appendChild(s);
  });
}
